import { ROOT_NODE, SerializedNodes, useEditor } from "@craftjs/core";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Collapse,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

import { PageService } from "@/services/admin-services";
import { CreatePage, Page } from "@/services/admin-services/models";
import { Language } from "@/services/global-services/models";
import { PageType } from "@/services/shared-models";
import { useAuth0 } from "@auth0/auth0-react";
import { ArchiveBoxArrowDownIcon, PaperAirplaneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import ClearIcon from "@mui/icons-material/Clear";
import { groupBy } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { ContentPage } from "../../base-pages/content";
import { DownloadsPage } from "../../base-pages/downloads";
import { FixedPage } from "../../base-pages/fixed";
import { EditableHomePage } from "../../base-pages/home";
import { EditableStep1Page } from "../../base-pages/step1";
import { EditableStep2Page } from "../../base-pages/step2";
import { EditableStep3Page } from "../../base-pages/step3";

export interface PageConfiguration {
    title: string;
    pageType: PageType;
    slug: string;
    language: string;
    parent: string;
    parentOrder: number;
    inverseOrder: boolean;
    description: string;
    hidden: boolean;
    comments: boolean;
}

interface NestedPage extends Page {
    path: string;
    level: number;
    circularRef?: boolean;
}

interface PageConfiguratorProps {
    configuration: PageConfiguration;
    languages: Language[];
}

const PageConfigurator = ({ configuration, languages }: PageConfiguratorProps) => {
    const [title, setTitle] = useState<string>("");
    const [allowComments, setAllowComments] = useState<boolean>(false);
    const [pageType, setPageType] = useState<PageType>();
    const [slug, setSlug] = useState<string>("");
    const [slugValid, setSlugValid] = useState<boolean>(true);
    const [slugUsed, setSlugUsed] = useState<boolean>(false);
    const [language, setLanguage] = useState<string>("int-en");
    const [parent, setParent] = useState<string>(configuration.parent || "");
    const [order, setOrder] = useState<number>(0);
    const [inverseOrder, setInverseOrder] = useState<boolean>(configuration.inverseOrder || false);
    const [description, setDescription] = useState<string>("");
    const [visibility, setVisibility] = useState<boolean>(true);
    const [pages, setPages] = useState<Array<Page>>([]);
    const [availablePages, setAvailablePages] = useState<Array<Page>>([]);
    const [nestedPages, setNestedPages] = useState<Record<string, Array<NestedPage>>>();

    const [previousPageContent, setPreviousPageContent] = useState<{
        home: SerializedNodes | null;
        step1: SerializedNodes | null;
        step2: SerializedNodes | null;
        step3: SerializedNodes | null;
        content: SerializedNodes | null;
        downloads: SerializedNodes | null;
        elements: SerializedNodes | null;
        fixed: SerializedNodes | null;
    }>({
        fixed: null,
        home: null,
        step1: null,
        step2: null,
        step3: null,
        content: null,
        downloads: null,
        elements: null,
    });

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<"publish" | "draft" | null>(null);
    const [savedNotificationOpen, setSavedNotificationOpen] = useState<boolean>(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const theme = useTheme();
    const { user } = useAuth0();
    const navigate = useNavigate();

    const pageService = useMemo(() => new PageService("int"), []);
    const { identifier } = useParams();

    const {
        actions: { setProp, deserialize },
        query: { node, serialize, getSerializedNodes },
        enabled,
    } = useEditor((state) => ({ enabled: state.options.enabled }));

    useEffect(() => {
        pageService.getPages().then((pages) => {
            setPages(pages);
            setIsLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const availablePages = pages.filter((page) => page.language === language && page.id !== identifier);
        const nestedPages = [...availablePages] as Array<NestedPage>;

        nestedPages.forEach((page) => {
            let url = `${page.parentOrder}-${page.slug}`;
            const parents = [];
            let parent = pages.find((x) => x.id === page.parentPageId);
            let level = 0;

            parents.push(parent);

            while (parent) {
                url = `${parent.parentOrder}-${parent.slug}/${url}`;
                level++;
                const parentId = parent.parentPageId;
                parent = pages.find((x) => x.id === parentId);
            
                if (parent) {
                    if (parents.findIndex((x) => x && x.id === parent!.id) > -1) {
                        console.warn("Detected circular parent reference for page:", page);
                        page.circularRef = true;
                        break;
                    }

                    parents.push(parent);
                }
            }

            page.path = url;
            page.level = level;
        });

        setNestedPages(groupBy(nestedPages, (x) => x.path));
        setAvailablePages(availablePages);
        setParent("");

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pages, language]);

    useEffect(() => {
        if (!configuration) return;

        setTitle(configuration.title);

        setPageType(PageType[configuration.pageType as keyof typeof PageType]);
        setSlug(configuration.slug);
        setLanguage(configuration.language);
        setOrder(configuration.parentOrder);
        setInverseOrder(configuration.inverseOrder);
        setDescription(configuration.description);
        setVisibility(!configuration.hidden);
        setAllowComments(configuration.comments);
    }, [configuration]);

    useEffect(() => {
        if (!configuration || !availablePages) return;

        setParent(configuration.parent);
    }, [configuration, availablePages]);

    useEffect(() => {
        if (!canUpdateNode()) return;

        setProp(ROOT_NODE, (props) => {
            props.title = title;
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title]);

    useEffect(() => {
        if (!enabled) return;
        if (!pageType) return;

        const pageNode = node(ROOT_NODE);
        const activePageType = pageNode.get()?.data.props.pageType;
        if (activePageType === pageType) return;

        const previousContent = { ...previousPageContent };
        switch (activePageType) {
            case PageType.Home:
                previousContent["home"] = getSerializedNodes();
                break;

            case PageType.Step1:
                previousContent["step1"] = getSerializedNodes();
                break;

            case PageType.Step2:
                previousContent["step2"] = getSerializedNodes();
                break;

            case PageType.Step3:
                previousContent["step3"] = getSerializedNodes();
                break;

            case PageType.Content:
                previousContent["content"] = getSerializedNodes();
                break;

            case PageType.Download:
                previousContent["downloads"] = getSerializedNodes();
                break;

            case PageType.Master:
            case PageType.Elements:
                previousContent["fixed"] = getSerializedNodes();
                break;
        }

        setPreviousPageContent(previousContent);

        let page: SerializedNodes | undefined;
        switch (pageType) {
            case PageType.Home:
                page = previousContent["home"] || EditableHomePage;
                break;

            case PageType.Step1:
                page = previousContent["step1"] || EditableStep1Page;
                break;

            case PageType.Step2:
                page = previousContent["step2"] || EditableStep2Page;
                break;

            case PageType.Step3:
                page = previousContent["step3"] || EditableStep3Page;
                break;

            case PageType.Content:
                page = previousContent["content"] || ContentPage;
                break;

            case PageType.Download:
                page = previousContent["downloads"] || DownloadsPage;
                break;

            case PageType.Master:
            case PageType.Elements:
                page = previousContent["fixed"] || FixedPage;
                break;
        }

        if (page) {
            page[ROOT_NODE].props.title = title;
            page[ROOT_NODE].props.pageType = pageType;
            page[ROOT_NODE].props.language = language;
            page[ROOT_NODE].props.parentPage = parent;
            page[ROOT_NODE].props.parentOrder = order;
            page[ROOT_NODE].props.inverseOrder = inverseOrder;
            page[ROOT_NODE].props.comments = allowComments ? "yes" : "no";
            deserialize(page);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, pageType]);

    useEffect(() => {
        if (!canUpdateNode()) return;

        setProp(ROOT_NODE, (props) => {
            props.language = language;
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    useEffect(() => {
        if (!canUpdateNode()) return;

        setProp(ROOT_NODE, (props) => {
            props.parentId = parent;
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parent]);

    useEffect(() => {
        if (!canUpdateNode()) return;

        setProp(ROOT_NODE, (props) => {
            props.parentOrder = order;
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order]);

    useEffect(() => {
        if (!canUpdateNode()) return;

        setProp(ROOT_NODE, (props) => {
            props.inverseOrder = inverseOrder;
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inverseOrder]);

    useEffect(() => {
        const valid = isSlugValid(slug);
        const used = isSlugUsed(slug);
        setSlugValid(valid && !used);
        setSlugUsed(used);

        if (!canUpdateNode() || !valid) return;

        setProp(ROOT_NODE, (props) => {
            props.slug = slug;
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, availablePages, enabled]);

    useEffect(() => {
        if (!canUpdateNode()) return;

        setProp(ROOT_NODE, (props) => {
            props.comments = allowComments ? "yes" : "no";
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowComments]);

    useEffect(() => {
        if (!savedNotificationOpen) return;

        timerRef.current = setTimeout(() => setSavedNotificationOpen(false), 3000);
    }, [savedNotificationOpen]);

    const canUpdateNode = (): boolean => enabled && node(ROOT_NODE).get() !== undefined;

    // const generateSlug = (title: string) => {
    //     return title
    //         .normalize("NFD")
    //         .replace(/[\u0300-\u036f]/g, "") //remove diacritics
    //         .toLowerCase()
    //         .replace(/\s+/g, "-") //spaces to dashes
    //         .replace(/&/g, "-and-") //ampersand to and
    //         .replace(/[^\w-]+/g, "") //remove non-words
    //         .replace(/--+/g, "-") //collapse multiple dashes
    //         .replace(/^-+/, "") //trim starting dash
    //         .replace(/-+$/, ""); //trim ending dash
    // };

    const isSlugValid = (slug: string): boolean => {
        return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
    };

    const isSlugUsed = (slug: string): boolean => {
        return availablePages.some((page) => page.slug === slug);
    };

    const onCancel = () => {
        navigate("..");
    };

    const onSave = (publish: boolean = false) => {
        setIsSaving(publish ? "publish" : "draft");
        setTimeout(() => setIsSaving(null), 1000);

        const data: CreatePage = {
            title: title,
            pageType:
                ((Object.keys(PageType) as Array<keyof typeof PageType>).find(
                    (key) => PageType[key] === pageType
                ) as PageType) || PageType.Home,
            language: language,
            slug: slug,
            description: description,
            content: serialize(),
            hidden: !visibility,
            parentPageId: parent ? parent : undefined,
            parentOrder: order,
            inverseOrder: inverseOrder,
            published: publish,
            user: user!.name!,
            translations: [],
            allowComments: allowComments,
        };

        let createOrUpdate;
        if (identifier) {
            createOrUpdate = pageService.updatePage(identifier, data);
        } else {
            createOrUpdate = pageService.createPage(data);
        }

        createOrUpdate.then((page) => {
            if (!identifier) navigate(`../${page.id}`, { replace: true });
            setSavedNotificationOpen(true);
            setIsSaving(null);
        });
    };

    const renderParentOptions = () => {
        if (!nestedPages || Object.keys(nestedPages).length === 0) return null;

        return Object.keys(nestedPages)
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
            .map((key) => {
                const page = nestedPages[key]![0];

                return (
                    <MenuItem key={page.id} value={page.id} sx={{ pl: 2 + page.level * 2 }}>
                        {page.title}
                    </MenuItem>
                );
            });
    };

    if (isLoading)
        return (
            <Grid container spacing={4} p={{ xs: 2, md: 4 }}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <Box sx={{ display: "flex", height: 400, alignItems: "center", flexDirection: "column" }}>
                        <Box sx={{ margin: "auto" }}>
                            <Typography>Loading data...</Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        );

    return (
        <Grid container spacing={2} p={{ xs: theme.spacing(0, 2), md: theme.spacing(0, 4) }}>
            <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="h6" gutterBottom fontWeight={700}>
                    Page information
                </Typography>
                <Typography variant="subtitle2" color={"text.secondary"}>
                    Define the page.
                </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Enter a title for the page
                        </Typography>
                        <TextField
                            label="Title"
                            variant="outlined"
                            fullWidth
                            value={title}
                            onChange={(e) => setTitle(e.currentTarget.value)}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Select a page type
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel id="demo-select-small">Type</InputLabel>
                            <Select
                                labelId="demo-select-small"
                                id="demo-select-small"
                                value={pageType}
                                label="Type"
                                onChange={(e) => setPageType(e.target.value as PageType)}
                            >
                                {Object.entries(PageType).map(([key, value]) => (
                                    <MenuItem value={value} key={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Enter a slug for the page
                        </Typography>
                        <TextField
                            label="Slug"
                            variant="outlined"
                            fullWidth
                            value={slug}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">/</InputAdornment>,
                            }}
                            onChange={(e) => setSlug(e.currentTarget.value)}
                            error={!slugValid && slug.length > 0}
                            helperText={
                                !slugValid && slug.length > 0
                                    ? slugUsed
                                        ? "A page with this slug already exists."
                                        : "Invalid format, only lowercase characters, numbers and hypens are allowed."
                                    : ""
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Select a language for the page
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel id="demo-select-small">Language</InputLabel>
                            <Select
                                labelId="demo-select-small"
                                id="demo-select-small"
                                value={language}
                                label="Language"
                                onChange={(e) => setLanguage(e.target.value)}
                            >
                                {languages.map((language) => (
                                    <MenuItem key={language.slug} value={language.slug}>
                                        {language.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Select a parent page (optional)
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel id="demo-select-small">Parent</InputLabel>
                            <Select
                                labelId="demo-select-small"
                                id="demo-select-small"
                                value={parent}
                                label="Parent"
                                onChange={(e) => setParent(e.target.value)}
                                endAdornment={
                                    <IconButton
                                        sx={{
                                            display: parent ? "" : "none",
                                            mr: 1,
                                            color: "text.secondary",
                                            "&:hover": { color: "text.primary" },
                                        }}
                                        onClick={() => setParent("")}
                                        disableRipple
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                }
                            >
                                {availablePages.length === 0 ? (
                                    <MenuItem disabled value="">
                                        No pages available
                                    </MenuItem>
                                ) : (
                                    renderParentOptions()
                                )}
                                {/* {availablePages.map((page) => (
                                    <MenuItem key={page.id} value={page.id}>
                                        {page.title}
                                    </MenuItem>
                                ))} */}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Enter an order for the page (optional)
                        </Typography>
                        <TextField
                            label="Order"
                            variant="outlined"
                            type="number"
                            fullWidth
                            value={order}
                            onChange={(e) => setOrder(parseInt(e.currentTarget.value))}
                        />
                    </Grid>

                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Inverse child order?
                        </Typography>
                        <Checkbox checked={inverseOrder} onChange={(e) => setInverseOrder(e.target.checked)} />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Page description (optional)
                        </Typography>
                        <TextField
                            label="Description"
                            variant="outlined"
                            fullWidth
                            value={description}
                            onChange={(e) => setDescription(e.currentTarget.value)}
                            multiline
                            rows={4}
                        />
                    </Grid>

                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Visibility
                        </Typography>
                        <FormControl fullWidth>
                            <RadioGroup
                                value={visibility}
                                onChange={(e) => setVisibility((e.target as HTMLInputElement).value === "true")}
                                name="visibility-buttons-group"
                            >
                                <FormControlLabel value={true} control={<Radio />} label="Visible" />
                                <FormControlLabel value={false} control={<Radio />} label="Hidden" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Allow comments?
                        </Typography>
                        <FormControl fullWidth>
                            <RadioGroup
                                value={allowComments}
                                onChange={(e) => setAllowComments((e.target as HTMLInputElement).value === "true")}
                                name="comments-buttons-group"
                            >
                                <FormControlLabel value={true} control={<Radio />} label="Yes" />
                                <FormControlLabel value={false} control={<Radio />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12 }} sx={{ display: "flex", alignContent: "flex-end" }}>
                        <Button
                            variant="contained"
                            color="error"
                            sx={{ ml: "auto" }}
                            onClick={() => onCancel()}
                            disabled={Boolean(isSaving)}
                        >
                            Cancel
                        </Button>
                        <Box sx={{ ml: 1, position: "relative" }}>
                            <Button
                                variant="contained"
                                startIcon={<ArchiveBoxArrowDownIcon className="h-5 w-5" />}
                                sx={{ ml: 1 }}
                                onClick={() => onSave()}
                                disabled={!slugValid || Boolean(isSaving)}
                            >
                                Save
                            </Button>
                            {isSaving === "draft" && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        marginTop: "-12px",
                                        marginLeft: "-12px",
                                    }}
                                />
                            )}
                        </Box>
                        <Box sx={{ ml: 1, position: "relative" }}>
                            <Button
                                variant="contained"
                                startIcon={<PaperAirplaneIcon className="h-5 w-5" />}
                                sx={{ ml: 1 }}
                                onClick={() => onSave(true)}
                                disabled={!slugValid || Boolean(isSaving)}
                            >
                                Save and publish
                            </Button>
                            {isSaving === "publish" && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        marginTop: "-12px",
                                        marginLeft: "-12px",
                                    }}
                                />
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            <Box sx={{ width: "100%" }}>
                <Collapse in={savedNotificationOpen}>
                    <Alert
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setSavedNotificationOpen(false);
                                }}
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </IconButton>
                        }
                        sx={{ mb: 2 }}
                    >
                        Succesfully saved changes.
                    </Alert>
                </Collapse>
            </Box>
        </Grid>
    );
};

export default PageConfigurator;
